import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, Upload, Loader2, ImagePlus } from "lucide-react";
import { LOGO_URL } from "@/data/content";
import { resolveImage } from "@/components/Realisations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-white border border-slate-300 focus:border-[#E31E24] focus:ring-2 focus:ring-[#E31E24]/15 outline-none px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors rounded-lg";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [place, setPlace] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadItems = () =>
    axios.get(`${API}/realisations`).then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadItems();
  }, [user]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) {
      toast.error("Ajoutez une photo et un titre.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("place", place.trim());
      await axios.post(`${API}/realisations`, fd, { withCredentials: true });
      toast.success("Photo ajoutée aux réalisations.");
      setTitle("");
      setPlace("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadItems();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API}/realisations/${id}`, { withCredentials: true });
      toast.success("Photo supprimée.");
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="animate-spin text-white" size={28} />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-slate-50 blueprint-grid-light flex items-center justify-center px-4">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white border border-slate-200 p-8 rounded-2xl shadow-sm"
        >
          <img src={LOGO_URL} alt="ACOMEP" className="h-10 w-auto mb-6" />
          <h1 className="font-display text-xl font-bold text-slate-900 mb-1">
            Espace administrateur
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Connectez-vous pour gérer les photos de réalisations.
          </p>
          <label className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
            E-mail
          </label>
          <input
            data-testid="admin-input-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputCls} mb-4`}
            placeholder="admin@acomep.be"
          />
          <label className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
            Mot de passe
          </label>
          <input
            data-testid="admin-input-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            placeholder="••••••••"
          />
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-[#E31E24] font-medium">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E31E24] hover:bg-[#C7181D] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full transition-colors"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            Se connecter
          </button>
          <a href="/" data-testid="admin-back-home" className="mt-4 block text-center text-xs text-slate-400 hover:text-slate-600">
            ← Retour au site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-lg p-1.5 inline-flex">
              <img src={LOGO_URL} alt="ACOMEP" className="h-6 w-auto" />
            </span>
            <span className="font-mono-tech text-[11px] tracking-[0.25em] uppercase text-white/60">
              Administration - Réalisations
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-view-site" className="text-xs text-white/60 hover:text-white transition-colors">
              Voir le site
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#E31E24] transition-colors"
            >
              <LogOut size={15} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <form
          onSubmit={onUpload}
          data-testid="admin-upload-form"
          className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-10"
        >
          <h2 className="font-display text-lg font-bold text-slate-900 mb-5 flex items-center gap-2">
            <ImagePlus size={19} className="text-[#E31E24]" /> Ajouter une photo de chantier
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                Photo *
              </label>
              <input
                ref={fileRef}
                data-testid="admin-input-file"
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-slate-950 file:text-white file:px-4 file:py-2 file:text-xs file:font-semibold hover:file:bg-[#E31E24] file:transition-colors"
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                Titre *
              </label>
              <input
                data-testid="admin-input-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex : Tableau électrique - rénovation"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                Lieu
              </label>
              <input
                data-testid="admin-input-place"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                placeholder="Ex : Bruxelles"
                className={inputCls}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-upload-submit"
            className="mt-5 inline-flex items-center gap-2 bg-[#E31E24] hover:bg-[#C7181D] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full transition-colors"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Publier la photo
          </button>
        </form>

        <h2 className="font-display text-lg font-bold text-slate-900 mb-5">
          Photos publiées ({items.length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-items-grid">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`admin-item-${item.id}`}
              className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden"
            >
              <img
                src={resolveImage(item.image_url)}
                alt={item.title}
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{item.title}</div>
                  <div className="text-xs text-slate-500">
                    {item.code}
                    {item.place ? ` - ${item.place}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => onDelete(item.id)}
                  data-testid={`admin-delete-${item.id}`}
                  aria-label={`Supprimer ${item.title}`}
                  className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:border-[#E31E24] hover:text-[#E31E24] transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
